import { FC } from 'react';
import EfSpec from '@elem/ui/ef-spec';
import EfImg from '@elem/ui/ef-img';
import EfLink from '@elem/ui/ef-link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import { useState, useEffect, useRef } from 'react';

import { useRouter } from 'next/router';
import { FormatMoneyLocal } from '@elem/compCommon/ShopifyShoppingCart/const';
import styles from './index.module.scss';
import { getSolarRecommand } from '@/service/solar-calculator';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';

type CardType = {
    productId: string;
    variantId: string;
    inventory?: number;
    img?: string;
    title?: string;
    price?: number;
    compareAtPrice?: number;
    link_buy?: string;
};

const G: FC = () => {
    const { locale = 'us' } = useRouter();
    const { t } = useLang('ces2025');
    const [gap, setGap] = useState(24);
    const swiperRef = useRef(null);

    const items: CardType[] = [
        {
            // DPU
            productId: '6964802453577',
            variantId: '40758830071881',
        },
        {
            // DP3
            productId: '7185477075017',
            variantId: '41385721004105',
        },
        {
            // 5kVA 48V Power Kits
            productId: '7352587878473',
            variantId: '41830211747913',
        },
        {
            // D3 + 800W AC
            productId: '7289136054345',
            variantId: '41826183249993',
        },
        {
            // RAPID
            productId: '7302855557193',
            variantId: '41843266584649',
        },
    ];

    const [cards, setCards] = useState<CardType[]>(items);

    const getOffCount = (cardData: CardType) => {
        if (cardData && cardData.compareAtPrice && cardData.price) {
            const offCount = (1 - cardData.price / cardData.compareAtPrice) * 100;
            return Math.round(offCount);
        }
        return 0;
    };

    const handleSelectProduct = (productName: string) => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'checkout',
            button_name: `${t('G_buy')}_${productName}`,
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const w = window.innerWidth;
            setGap(w <= 1024 ? 12 : w <= 1440 ? 16 : 24);
        }
    }, []);

    useEffect(() => {
        const tempArr = JSON.parse(JSON.stringify(items));
        const getAsynItem = async (productId: string, variantId: string, k: number) => {
            return await new Promise(async (resolve, reject) => {
                try {
                    const res = await getSolarRecommand({ productId, variantId }, locale);
                    if (res?.code === '0') {
                        if (res?.data?.productTitle || res?.data?.skuTitle) {
                            tempArr[k].title = res?.data?.productTitle || res?.data?.skuTitle;
                        }
                        if (res?.data?.variantImg) {
                            tempArr[k].img = res?.data?.variantImg;
                        }
                        tempArr[k].price = res?.data?.price;
                        tempArr[k].compareAtPrice = res?.data?.compareAtPrice || res?.data?.price;
                        tempArr[k].inventory = res?.data?.inventoryQuantity ?? undefined;
                        // tempArr[k].inventory = 1;
                        tempArr[k].link_buy = `https://${locale}.ecoflow.com/cart/${tempArr[k].variantId}:1?channel=buy_button`;
                        return resolve(res);
                    } else {
                        console.error(res?.message);
                        return reject(res?.message);
                    }
                } catch (e) {
                    console.error(e);
                    return reject(e);
                }
            });
        };

        const promises: any[] = [];
        tempArr?.map((item: CardType, k: number) => {
            if (item?.variantId) {
                promises.push(getAsynItem(item?.productId, item.variantId, k));
            }
        });

        // const promises = tempArr?.map((item: any, k: number) => getAsynItem(item?.productId, item.variantId, k));
        Promise.all(promises)
            .then(() => {
                setCards(tempArr?.filter((list: any) => list?.inventory !== undefined));
            })
            .catch((e) => {
                console.error(e);
            });
    }, []);

    useEffect(() => {
        const swiper = swiperRef.current.swiper;

        const updateButtonState = () => {
            const prevButton = document.querySelector('#CES-2025-G .swiper-button-prev') as HTMLElement;
            const nextButton = document.querySelector('#CES-2025-G .swiper-button-next') as HTMLElement;

            if (swiper.isBeginning) {
                prevButton.classList.add('swiper-button-disabled');
            } else {
                prevButton.classList.remove('swiper-button-disabled');
            }

            if (swiper.isEnd) {
                nextButton.classList.add('swiper-button-disabled');
            } else {
                nextButton.classList.remove('swiper-button-disabled');
            }
        };

        swiper.on('slideChange', updateButtonState);

        updateButtonState();

        return () => {
            swiper.off('slideChange', updateButtonState);
        };
    }, []);

    return (
        <section className={styles.section} id="CES-2025-G">
            <EfSpec />
            <p className="sec-title">{t('G_title')}</p>
            <div className="box">
                <Swiper ref={swiperRef} className="cards" modules={[Navigation]} spaceBetween={gap} navigation={false} slidesPerView={'auto'} initialSlide={0}>
                    {cards?.map((card: any, k: number) => (
                        <SwiperSlide className={`card`} key={k}>
                            <div className="top">
                                {card?.price !== card?.compareAtPrice && (
                                    <span className="card-tag">
                                        <span className="num">{getOffCount(card)}%</span>
                                        <span className="unit">{t('G_tag')}</span>
                                    </span>
                                )}
                                <div className="card-img">
                                    <EfImg pc={card?.img} />
                                </div>
                                <p className="card-title">{card?.title}</p>
                            </div>
                            <div className="bottom">
                                <div className="card-price">
                                    <span className="price">{FormatMoneyLocal(card?.price, locale)}</span>
                                    {card?.price !== card?.compareAtPrice && (
                                        <span className="compare-price">{FormatMoneyLocal(card?.compareAtPrice, locale)}</span>
                                    )}
                                </div>
                                <EfLink className="card-buy" href={card?.link_buy} target="_blank" onClick={() => handleSelectProduct(card?.title)}>
                                    {t('G_buy')}
                                </EfLink>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <div className="swiper-navigation">
                    <button className="swiper-button-prev" onClick={() => swiperRef.current.swiper.slidePrev()}></button>
                    <button className="swiper-button-next" onClick={() => swiperRef.current.swiper.slideNext()}></button>
                </div>
            </div>
        </section>
    );
};

export default G;
